import L from 'leaflet'

import {
  beginCameraIntent,
  ENTITY_POINT_ZOOM,
  forceFitBounds,
  forceSetView,
  type CameraFocusAnchor,
} from '../mapCamera'

/** Pixel radius for grouping nearby markers into a cluster. */
const CLUSTER_RADIUS_PX = 28
const CLUSTER_RADIUS_SQ = CLUSTER_RADIUS_PX * CLUSTER_RADIUS_PX
/** Grid cell size for neighbor lookup (same as radius → 3×3 neighborhood). */
const GRID_CELL_PX = CLUSTER_RADIUS_PX
/** Treat members as coincident below this span (degrees). */
const POINT_BOUNDS_EPS = 1e-7
/** Keep pins clear of the frame edge (icon radius + breathing room). */
const MARKER_COMFORT_PAD_PX = 40
/** Extra top inset for the omni search bar / map chrome. */
const TOP_CHROME_PAD_PX = 72
/** Mobile inset when fitting to full width / top half. */
const MOBILE_EDGE_PAD_PX = 56
/** Desktop: keep content in the middle ~40% (30% pad each side). */
const DESKTOP_FRAME_PAD_FRAC = 0.3

export type ClusterableMarker = {
  key: string
  lat: number
  lng: number
  /** Display label for coincident-cluster picker. */
  title: string
  subtitle?: string
  color: string
}

type ProjectedMarker = ClusterableMarker & { point: L.Point; index: number }

export type MarkerCluster = {
  id: string
  markers: ClusterableMarker[]
  count: number
  /** Geographic center (average of members). */
  lat: number
  lng: number
}

export type MarkerClusterLayout = {
  clusters: MarkerCluster[]
  /** Marker keys that should render as individual pins. */
  unclusteredKeys: Set<string>
}

/** How cluster zoom should frame members in the viewport. */
export type ClusterZoomViewport = {
  isMobile: boolean
  /** Mobile operations output panel open — frame into the top half. */
  outputOpen: boolean
}

export type ClusterZoomFrame = {
  paddingTopLeft: L.Point
  paddingBottomRight: L.Point
  focusAnchor: CameraFocusAnchor
}

function comfortPad(base: number): number {
  return Math.max(base, MARKER_COMFORT_PAD_PX)
}

/**
 * Desktop: fit members into the center of the screen with generous side padding.
 * Mobile: fit to map width with edge padding so pins aren't on the rim.
 * Mobile + output panel: fit into the top half with the same edge padding.
 * All modes add extra top padding for the search bar chrome.
 */
export function resolveClusterZoomFrame(
  map: L.Map,
  viewport: ClusterZoomViewport,
): ClusterZoomFrame {
  const size = map.getSize()
  if (!viewport.isMobile) {
    const padX = comfortPad(size.x * DESKTOP_FRAME_PAD_FRAC)
    const padY = comfortPad(size.y * DESKTOP_FRAME_PAD_FRAC)
    return {
      paddingTopLeft: L.point(padX, padY + TOP_CHROME_PAD_PX),
      paddingBottomRight: L.point(padX, padY),
      focusAnchor: { x: 0.5, y: 0.5 },
    }
  }
  if (viewport.outputOpen) {
    const edge = comfortPad(MOBILE_EDGE_PAD_PX)
    return {
      paddingTopLeft: L.point(edge, edge + TOP_CHROME_PAD_PX),
      // Reserve the bottom half for the output panel, plus comfort inset.
      paddingBottomRight: L.point(edge, size.y * 0.5 + edge),
      focusAnchor: { x: 0.5, y: 0.25 },
    }
  }
  const edge = comfortPad(MOBILE_EDGE_PAD_PX)
  return {
    paddingTopLeft: L.point(edge, edge + TOP_CHROME_PAD_PX),
    paddingBottomRight: L.point(edge, edge),
    focusAnchor: { x: 0.5, y: 0.5 },
  }
}

function distanceSq(a: L.Point, b: L.Point): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return dx * dx + dy * dy
}

function isCoincident(markers: ClusterableMarker[]): boolean {
  const first = markers[0]
  for (let i = 1; i < markers.length; i += 1) {
    const m = markers[i]
    if (
      Math.abs(m.lat - first.lat) > POINT_BOUNDS_EPS ||
      Math.abs(m.lng - first.lng) > POINT_BOUNDS_EPS
    ) {
      return false
    }
  }
  return true
}

/** True when every member shares the same lat/lng (zoom cannot separate them). */
export function isClusterCoincident(cluster: MarkerCluster): boolean {
  return cluster.markers.length >= 2 && isCoincident(cluster.markers)
}

/** True when another marker shares this marker's exact lat/lng. */
export function hasCoincidentNeighbor(
  markers: ClusterableMarker[],
  key: string,
): boolean {
  const marker = markers.find((m) => m.key === key)
  if (!marker) return false
  return markers.some(
    (m) =>
      m.key !== key &&
      Math.abs(m.lat - marker.lat) <= POINT_BOUNDS_EPS &&
      Math.abs(m.lng - marker.lng) <= POINT_BOUNDS_EPS,
  )
}

function memberBounds(markers: ClusterableMarker[]): L.LatLngBounds {
  return L.latLngBounds(markers.map((m) => L.latLng(m.lat, m.lng)))
}

function isPointLikeBounds(bounds: L.LatLngBounds): boolean {
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  return (
    Math.abs(ne.lat - sw.lat) < POINT_BOUNDS_EPS &&
    Math.abs(ne.lng - sw.lng) < POINT_BOUNDS_EPS
  )
}

/**
 * Uncluster only when zoom can actually separate members.
 * Exact same-location piles always stay clustered (picker menu handles selection).
 */
export function shouldUnclusterAtZoom(map: L.Map, markers: ClusterableMarker[]): boolean {
  if (markers.length < 2) return true
  if (isCoincident(markers)) return false
  return map.getZoom() >= map.getMaxZoom() - 0.01
}

/** Stable short id — avoids megabyte React keys from joining every member key. */
function clusterIdFor(group: ProjectedMarker[]): string {
  let hash = 2166136261
  const ordered = group.map((m) => m.key).sort()
  for (const key of ordered) {
    for (let i = 0; i < key.length; i += 1) {
      hash ^= key.charCodeAt(i)
      hash = Math.imul(hash, 16777619)
    }
    hash ^= 124
    hash = Math.imul(hash, 16777619)
  }
  return `c${hash >>> 0}:${group.length}`
}

/**
 * Grid-accelerated BFS clustering in layer-pixel space.
 * Neighbor search is O(k) per marker via a spatial hash instead of O(n²).
 */
function buildProximityGroups(markers: ProjectedMarker[]): ProjectedMarker[][] {
  if (markers.length === 0) return []

  const grid = new Map<string, number[]>()
  for (let i = 0; i < markers.length; i += 1) {
    const p = markers[i].point
    const cellKey = `${Math.floor(p.x / GRID_CELL_PX)}:${Math.floor(p.y / GRID_CELL_PX)}`
    const bucket = grid.get(cellKey)
    if (bucket) bucket.push(i)
    else grid.set(cellKey, [i])
  }

  const visited = new Uint8Array(markers.length)
  const groups: ProjectedMarker[][] = []

  for (let startIdx = 0; startIdx < markers.length; startIdx += 1) {
    if (visited[startIdx]) continue
    visited[startIdx] = 1
    const group: ProjectedMarker[] = [markers[startIdx]]
    const queue = [startIdx]

    for (let qi = 0; qi < queue.length; qi += 1) {
      const current = markers[queue[qi]]
      const cx = Math.floor(current.point.x / GRID_CELL_PX)
      const cy = Math.floor(current.point.y / GRID_CELL_PX)
      for (let ox = -1; ox <= 1; ox += 1) {
        for (let oy = -1; oy <= 1; oy += 1) {
          const bucket = grid.get(`${cx + ox}:${cy + oy}`)
          if (!bucket) continue
          for (let bi = 0; bi < bucket.length; bi += 1) {
            const candidateIdx = bucket[bi]
            if (visited[candidateIdx]) continue
            const candidate = markers[candidateIdx]
            if (distanceSq(current.point, candidate.point) > CLUSTER_RADIUS_SQ) continue
            visited[candidateIdx] = 1
            group.push(candidate)
            queue.push(candidateIdx)
          }
        }
      }
    }

    groups.push(group)
  }

  return groups
}

export function buildMarkerClusters(
  map: L.Map | null,
  markers: ClusterableMarker[],
  options?: { excludeKeys?: ReadonlySet<string> },
): MarkerClusterLayout {
  const excludeKeys = options?.excludeKeys
  const unclusteredKeys = new Set<string>()
  const clusterable: ClusterableMarker[] = []

  for (const marker of markers) {
    if (excludeKeys?.has(marker.key)) {
      unclusteredKeys.add(marker.key)
      continue
    }
    clusterable.push(marker)
  }

  if (!map || clusterable.length < 2) {
    for (const marker of clusterable) unclusteredKeys.add(marker.key)
    return { clusters: [], unclusteredKeys }
  }

  const projected: ProjectedMarker[] = clusterable.map((marker, index) => ({
    ...marker,
    index,
    point: map.latLngToLayerPoint([marker.lat, marker.lng]),
  }))

  const clusters: MarkerCluster[] = []

  for (const group of buildProximityGroups(projected)) {
    if (group.length < 2 || shouldUnclusterAtZoom(map, group)) {
      for (const marker of group) unclusteredKeys.add(marker.key)
      continue
    }

    let sumLat = 0
    let sumLng = 0
    const members: ClusterableMarker[] = new Array(group.length)
    for (let i = 0; i < group.length; i += 1) {
      const marker = group[i]
      sumLat += marker.lat
      sumLng += marker.lng
      members[i] = {
        key: marker.key,
        lat: marker.lat,
        lng: marker.lng,
        title: marker.title,
        subtitle: marker.subtitle,
        color: marker.color,
      }
    }
    const n = group.length
    clusters.push({
      id: clusterIdFor(group),
      markers: members,
      count: n,
      lat: sumLat / n,
      lng: sumLng / n,
    })
  }

  return { clusters, unclusteredKeys }
}

/** Zoom the map to reveal cluster members within the requested viewport frame. */
export function zoomToCluster(
  map: L.Map,
  cluster: MarkerCluster,
  viewport: ClusterZoomViewport = { isMobile: false, outputOpen: false },
): void {
  beginCameraIntent(`cluster:${cluster.id}:${Date.now()}`)
  const bounds = memberBounds(cluster.markers)
  if (!bounds.isValid()) return

  const frame = resolveClusterZoomFrame(map, viewport)

  if (isPointLikeBounds(bounds)) {
    const nextZoom = Math.min(
      map.getMaxZoom(),
      Math.max(map.getZoom() + 2, ENTITY_POINT_ZOOM),
    )
    forceSetView(map, cluster.lat, cluster.lng, nextZoom, {
      focusAnchor: frame.focusAnchor,
    })
    return
  }

  // Fit into the framed region. Cap zoom at the padded fit level so we don't
  // over-zoom and pin members against the frame edge. Still advance at least
  // one step when the padded fit wouldn't move (dense piles).
  const pad = L.point(
    Math.max(frame.paddingTopLeft.x, frame.paddingBottomRight.x),
    Math.max(frame.paddingTopLeft.y, frame.paddingBottomRight.y),
  )
  const boundsZoom = map.getBoundsZoom(bounds, false, pad)
  // Prefer the padded fit zoom; only force +1 when already at/above that zoom
  // (otherwise +1 can overshoot and hug the edges).
  const nextZoom = Math.min(
    map.getMaxZoom(),
    map.getZoom() + 0.05 >= boundsZoom
      ? Math.max(boundsZoom, map.getZoom() + 1)
      : boundsZoom,
  )

  forceFitBounds(map, bounds, {
    maxZoom: nextZoom,
    paddingTopLeft: frame.paddingTopLeft,
    paddingBottomRight: frame.paddingBottomRight,
  })
}
